import { Autocomplete, Button } from '@mantine/core';
import { type KeyboardEvent, type ReactNode, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './FormCreate.module.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isEmailAddress(value: string): boolean {
  return EMAIL_RE.test(value.trim());
}

export interface AddressInputProps {
  label: ReactNode;
  /** Addresses offered while typing: the account's notification addresses not already chosen. */
  suggestions: string[];
  /** Resolve when the address was taken; reject to keep the text in the input. */
  onAdd: (email: string) => Promise<void> | void;
  placeholder?: string;
  help?: ReactNode;
  /** Shows an Add button next to the input. */
  withButton?: boolean;
  buttonLabel?: string;
  disabled?: boolean;
}

/** One input for both: pick from the pool, or type a new address. */
export function AddressInput({
  label,
  suggestions,
  onAdd,
  placeholder,
  help,
  withButton,
  buttonLabel,
  disabled,
}: AddressInputProps) {
  const { t } = useTranslation();
  const id = useId();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Enter on a highlighted suggestion fires both the option submit and the keydown.
  const inFlight = useRef(false);

  const add = async (raw: string) => {
    const email = raw.trim();
    if (!email || inFlight.current) return;
    if (!isEmailAddress(email)) {
      setError(t('create.invalidEmail'));
      return;
    }
    inFlight.current = true;
    setBusy(true);
    try {
      await onAdd(email);
      setValue('');
      setError(null);
    } catch {
      // the caller reports why; the text stays for another try
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void add(e.currentTarget.value);
    }
  };

  return (
    <div className={classes.field}>
      <label htmlFor={id} className={classes.fieldLabel}>
        {label}
      </label>
      <div className={classes.inline}>
        <Autocomplete
          id={id}
          className={classes.grow}
          value={value}
          onChange={(next) => {
            setValue(next);
            setError(null);
          }}
          onOptionSubmit={(option) => void add(option)}
          onKeyDown={onKeyDown}
          data={suggestions}
          placeholder={placeholder}
          error={error}
          disabled={disabled || busy}
          autoComplete="off"
        />
        {withButton && (
          <Button onClick={() => void add(value)} loading={busy} disabled={disabled}>
            {buttonLabel ?? t('ui.add')}
          </Button>
        )}
      </div>
      {help && <span className={classes.help}>{help}</span>}
    </div>
  );
}
